import classnames from 'classnames';
import lodash from 'lodash';
import React, { PureComponent } from 'react';
import ReactModal from 'react-modal';

import { withRicosContext, withZIndexContext } from './../../../ricos-context';
import type { TFunction } from './../../../ricos-types';

import { POLLS_VOTED_USER_ITEM } from '../../data-hooks';
import type { PollsMember } from '../../types';
import { withRCEHelpers } from '../rce-helpers-context';
import styles from './voted-users.rtlignore-scss';
import { VotedUsersModal } from './VotedUsersModal';
import VoterAvatar from './VoterAvatar';

type VotedUserProps = {
  option: { count: number; anonymousCount: number; latestVoters: string[] };
  showVoters?: boolean;
  showResults?: boolean;
  showVotes?: boolean;
  isMobile?: boolean;
  zIndexService: { getZIndex: (type: string) => number };
  rce: {
    isViewMode?: boolean;
    preventInteraction?: boolean;
    getSiteMembers: (memberIds: string[]) => Promise<PollsMember[]>;
    isWebView?: boolean;
  };
  fetchVoters: (args: {
    cursor?: string;
    limit: number;
  }) => Promise<{ voters: { memberId: string }[]; moreItems: boolean }>;
  t: TFunction;
};

class VotedUsersComponent extends PureComponent<
  VotedUserProps,
  { isOpen: boolean; $container: HTMLDivElement | null; latestVotedMembers: PollsMember[] }
> {
  state = {
    isOpen: false,
    $container: null,
    latestVotedMembers: [],
  };

  async getSiteMember(id: string) {
    const siteMembers = (await this.props.rce.getSiteMembers?.([id])) || [];
    return siteMembers.find(siteMember => siteMember.siteMemberId === id);
  }

  componentDidMount() {
    this.fetchLastVotedMembers();
  }

  componentDidUpdate(prevProps: $TSFixMe) {
    if (lodash.isEqual(prevProps.option.latestVoters, this.props.option.latestVoters)) {
      return;
    }
    this.fetchLastVotedMembers();
  }

  fetchLastVotedMembers(): Promise<void> {
    const { rce, option, showVoters } = this.props;

    if (!showVoters || !rce.getSiteMembers || !option.latestVoters || !option.latestVoters.length) {
      return Promise.resolve();
    }
    return rce
      .getSiteMembers(option.latestVoters)
      .then(siteMembers => {
        this.setState({ latestVotedMembers: siteMembers });
      })
      .catch(e => {
        console.error('Failed to fetch site members', option.latestVoters, e);
      });
  }

  renderMember(member: $TSFixMe) {
    if (!member) {
      return null;
    }

    return (
      <li key={member.siteMemberId} className={styles.avatar}>
        <VoterAvatar
          className={styles.avatar}
          member={member}
          src={member.imageUrl}
          size={{
            width: 20,
            height: 20,
          }}
        />
      </li>
    );
  }

  closeModal = () => this.setState({ isOpen: false });

  openModal = (e: $TSFixMe) => {
    const { showVoters, rce } = this.props;
    e.preventDefault();

    if (e.keyCode || !showVoters || rce.preventInteraction) {
      return;
    }

    this.setState({ isOpen: !!this.props.rce.isViewMode });
  };

  render() {
    const { option, t, showResults, showVotes, showVoters, fetchVoters, rce, isMobile } =
      this.props;
    const { isOpen, $container } = this.state;

    if (!showResults || !showVotes || !option.count) {
      return null;
    }

    const zIndex = this.props.zIndexService.getZIndex(isMobile ? 'DRAWER' : 'DIALOG');

    return (
      <>
        <div
          className={classnames(styles.container, {
            [styles.cta]: showVoters && option.latestVoters?.length,
            [styles.webview]: rce.isWebView,
          })}
          ref={$container => this.setState({ $container })}
          onClick={this.openModal}
          onKeyPress={this.openModal}
          role="button"
          aria-haspopup="dialog"
          tabIndex={0}
        >
          <ul className={styles.avatar_list}>
            {this.state.latestVotedMembers.map(this.renderMember)}
          </ul>
          <span data-hook={POLLS_VOTED_USER_ITEM}>
            {t('Poll_Viewer_VoteCount', { number: option.count || 0 })}
          </span>
        </div>

        {$container && option.latestVoters?.length ? (
          <ReactModal
            isOpen={isOpen}
            onRequestClose={this.closeModal}
            parentSelector={() => document.body}
            className={classnames(styles.modal)}
            overlayClassName={classnames(styles.overlay)}
            style={{ overlay: { zIndex } }}
          >
            <VotedUsersModal
              option={option}
              fetchVoters={fetchVoters}
              onRequestClose={this.closeModal}
            />
          </ReactModal>
        ) : null}
      </>
    );
  }
}

export const VotedUsers = withRicosContext(withZIndexContext(withRCEHelpers(VotedUsersComponent)));
